import { coordsOf, isOnline, startMinute, type Opportunity } from '../../shared/opportunity';
import { attendedSpan, type Trims } from './attendance';
import { isSamePlace, pointFor } from './travel';

/**
 * The day's journey, for MAP MY DAY: where the user's scheduled events are, in
 * the order the day reaches them, from the hotel and back when one is set.
 *
 * Events that overlap cannot both be a stop on one journey. Each keeps its own
 * pin, ringed red, and the leg between them is red: that is the leg that cannot
 * be made. Only at one place do they become one stop, "3·4" (Sam, Sept 18). A
 * connection that is tight but possible is an ordinary leg, as `conflicts.ts`
 * separates overlaps from tight connections.
 */

/** Where the user is staying: the route's first and last stop, when set. */
export interface Home {
  label: string;
  coords: [number, number];
}

export interface RouteStop {
  key: string;
  point: [number, number];
  /** The hotel, drawn as a house rather than a number. */
  home: boolean;
  /** The events at this stop, with their numbers: from 1 on the first event. */
  events: { event: Opportunity; number: number }[];
  /** Only the neighborhood is known, so the pin is its centre and is drawn hollow. */
  approximate: boolean;
  /** The events here that run at the same time as another on the route, by name. */
  clashesWith: Opportunity[];
}

export interface RouteLeg {
  from: number;
  to: number;
  /** Events at either end that run at the same time: while there are any, this leg cannot be made. */
  clashes: [Opportunity, Opportunity][];
}

export type OffRouteReason = 'online' | 'untimed' | 'unplaced';

export interface DayRoute {
  stops: RouteStop[];
  legs: RouteLeg[];
  /** Scheduled that day but not on the route, in the day's order. */
  offRoute: { event: Opportunity; why: OffRouteReason }[];
}

export function buildDayRoute(events: Opportunity[], trims: Trims, home: Home | null): DayRoute {
  const span = (e: Opportunity) => attendedSpan(e, trims);
  const byTime = [...events].sort((a, b) => span(a).start - span(b).start || span(a).end - span(b).end);

  const offRoute: DayRoute['offRoute'] = [];
  const routed: { event: Opportunity; point: [number, number] }[] = [];
  for (const event of byTime) {
    const point = pointFor(event);
    if (isOnline(event)) offRoute.push({ event, why: 'online' });
    else if (event.isAllDay || startMinute(event) == null) offRoute.push({ event, why: 'untimed' });
    else if (!point) offRoute.push({ event, why: 'unplaced' });
    else routed.push({ event, point });
  }

  // Touching at the boundary is a handover, not a clash, as everywhere else.
  const overlap = (a: Opportunity, b: Opportunity) =>
    a.id !== b.id && span(a).start < span(b).end && span(b).start < span(a).end;

  const stops: RouteStop[] = [];
  routed.forEach(({ event, point }, i) => {
    const entry = { event, number: i + 1 };
    const approximate = !coordsOf(event);
    const clashesWith = routed.map((r) => r.event).filter((other) => overlap(event, other));
    const last = stops[stops.length - 1];
    // One after another at one place is one stop.
    if (last && !last.home && isSamePlace(last.point, point) && last.approximate === approximate) {
      last.events.push(entry);
      last.clashesWith.push(...clashesWith.filter((c) => !last.clashesWith.includes(c)));
      return;
    }
    stops.push({ key: event.id, point, home: false, events: [entry], approximate, clashesWith });
  });

  if (home && stops.length > 0) {
    const house = (key: string): RouteStop => ({
      key,
      point: home.coords,
      home: true,
      events: [],
      approximate: false,
      clashesWith: [],
    });
    stops.unshift(house('home-out'));
    stops.push(house('home-back'));
  }

  const legs: RouteLeg[] = [];
  for (let i = 1; i < stops.length; i += 1) {
    const clashes: [Opportunity, Opportunity][] = [];
    for (const { event: a } of stops[i - 1].events) {
      for (const { event: b } of stops[i].events) if (overlap(a, b)) clashes.push([a, b]);
    }
    legs.push({ from: i - 1, to: i, clashes });
  }

  return { stops, legs, offRoute };
}
